import React, { useState } from 'react';
import {
  Typography,
  Container,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
} from '@mui/material';
import { useUsersQuery, useAddUserRoleMutation, useRemoveUserRoleMutation } from '../../hooks/api';

import { useAuth } from '../../contexts/AuthContext';
import UsersTable from './UsersTable';
import CreateUserDialog from './CreateUserDialog';
import DeleteUserDialog from './DeleteUserDialog';

const UserManagementPage: React.FC = () => {
  // Use React Query hooks
  const { data: users = [], isLoading: loading, error } = useUsersQuery();
  const addRoleMutation = useAddUserRoleMutation();
  const removeRoleMutation = useRemoveUserRoleMutation();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);

  const { hasRole } = useAuth();

  // Define available roles
  const availableRoles = ['SystemAdministrators', 'CampaignManagers', 'DataAnalysts'];

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleRoleChange = async (username: string, role: string, checked: boolean) => {
    try {
      if (checked) {
        await addRoleMutation.mutateAsync({ username, role });
      } else {
        await removeRoleMutation.mutateAsync({ username, role });
      }

      showSnackbar(
        `Successfully ${checked ? 'added' : 'removed'} role "${role}" ${checked ? 'to' : 'from'} user`,
        'success'
      );
    } catch (err) {
      console.error(`Error updating user role:`, err);
      showSnackbar(`Failed to ${checked ? 'add' : 'remove'} role. Please try again.`, 'error');
    }
  };

  const handleDeleteSuccess = (username: string) => {
    showSnackbar(`User ${username} deleted successfully`, 'success');
  };

  const handleCreateSuccess = () => {
    showSnackbar('User created successfully', 'success');
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const openDeleteDialog = (username: string) => {
    setUserToDelete(username);
    setDeleteDialogOpen(true);
  };

  // Only System Administrators can access this page
  if (!hasRole('SystemAdministrators')) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don&apos;t have permission to access this page. This page is restricted to System
          Administrators.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        User Management
      </Typography>

      <Typography variant="body1" paragraph>
        Manage user roles and permissions from this page. Only System Administrators can modify user
        roles.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || 'An error occurred while loading users'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" color="primary" onClick={() => setNewUserDialogOpen(true)}>
          Create New User
        </Button>
      </Box>

      {loading && users.length === 0 ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <UsersTable
          users={users}
          availableRoles={availableRoles}
          onRoleChange={handleRoleChange}
          onDeleteUser={openDeleteDialog}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <CreateUserDialog
        open={newUserDialogOpen}
        onClose={() => setNewUserDialogOpen(false)}
        onSuccess={handleCreateSuccess}
        onError={message => showSnackbar(message, 'error')}
        loading={dialogLoading}
        setLoading={setDialogLoading}
      />

      <DeleteUserDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onSuccess={handleDeleteSuccess}
        onError={message => showSnackbar(message, 'error')}
        username={userToDelete}
        loading={dialogLoading}
        setLoading={setDialogLoading}
      />
    </Container>
  );
};

export default UserManagementPage;
